import axios from 'axios'
import {
  commonHeadersWeb,
  getCountryCode,
  getMainLanguageCode,
  publicApiURL,
} from 'src/consumer/services'

const CARE_ORCHESTRATION_BASE = '/api/public/care-orchestration'

function buildHeaders() {
  const countryCode = getCountryCode()
  const languageCode = getMainLanguageCode()
  return {
    ...commonHeadersWeb,
    'Content-Type': 'application/json',
    'ef-user-country-code': countryCode,
    'ef-app-language': languageCode,
  }
}

function extractCommonData(response) {
  return response?.data?.data || {}
}

function toErrorMessage(error, fallbackMessage) {
  const data = error?.response?.data
  return (
    data?.message ||
    data?.data?.cause ||
    data?.cause ||
    error?.message ||
    fallbackMessage
  )
}

export async function startCareIntake(payload) {
  try {
    const response = await axios.post(
      `${publicApiURL}${CARE_ORCHESTRATION_BASE}/intake/start`,
      payload,
      { headers: buildHeaders() },
    )
    const data = extractCommonData(response)
    return {
      careCase: data.careCase || null,
      caseId: data.caseId || data.careCase?.id || null,
    }
  } catch (error) {
    throw new Error(toErrorMessage(error, 'We could not start the care intake right now.'))
  }
}

export async function completeCareIntake(caseId) {
  try {
    const response = await axios.post(
      `${publicApiURL}${CARE_ORCHESTRATION_BASE}/intake/complete`,
      { caseId },
      { headers: buildHeaders() },
    )
    return extractCommonData(response).result || null
  } catch (error) {
    throw new Error(toErrorMessage(error, 'We could not complete the care intake right now.'))
  }
}

// Update structured answers on an existing case (e.g. user revises choices
// mid-flow after the first `startCareIntake`). Backend: POST intake/update.
export async function updateCareIntake(payload) {
  try {
    const response = await axios.post(
      `${publicApiURL}${CARE_ORCHESTRATION_BASE}/intake/update`,
      payload,
      { headers: buildHeaders() },
    )
    const data = extractCommonData(response)
    return {
      careCase: data.careCase || null,
      caseId: data.caseId || data.careCase?.id || null,
    }
  } catch (error) {
    throw new Error(toErrorMessage(error, 'We could not update the care intake right now.'))
  }
}

// Re-run triage for an existing case. Useful when the intake has changed or
// the coordinator wants a fresh recommendation. Backend: POST triage/run.
export async function runCareTriage(caseId) {
  try {
    const response = await axios.post(
      `${publicApiURL}${CARE_ORCHESTRATION_BASE}/triage/run`,
      { caseId },
      { headers: buildHeaders() },
    )
    return extractCommonData(response).result || null
  } catch (error) {
    throw new Error(toErrorMessage(error, 'We could not run triage right now.'))
  }
}

// Fetch the current recommendation + outputs for a case. Backend: GET
// recommendation/{caseId}.
export async function getCareRecommendation(caseId) {
  try {
    const response = await axios.get(
      `${publicApiURL}${CARE_ORCHESTRATION_BASE}/recommendation/${encodeURIComponent(caseId)}`,
      { headers: buildHeaders() },
    )
    return extractCommonData(response).result || null
  } catch (error) {
    throw new Error(toErrorMessage(error, 'We could not fetch the recommendation right now.'))
  }
}

// Fetch the current state of an intake case (answers, status, recommendation
// if any). Backend: GET intake/{caseId}.
export async function getCareIntake(caseId) {
  try {
    const response = await axios.get(
      `${publicApiURL}${CARE_ORCHESTRATION_BASE}/intake/${encodeURIComponent(caseId)}`,
      { headers: buildHeaders() },
    )
    return extractCommonData(response).result || null
  } catch (error) {
    throw new Error(toErrorMessage(error, 'We could not fetch the care intake right now.'))
  }
}
